import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import api from '../../services/api';
import ListLoadError from '../../components/admin/ListLoadError';
import styles from './CSVPagesPage.module.css';

const generateSlug = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Where does Upload History live, relative to wherever THIS page is rendered?
 *
 * This page is routed standalone at /admin/csv-pages and is ALSO composed by
 * import into a tabbed hub. The destination is derived from the current
 * pathname instead of being named, so the link resolves under either host and
 * keeps resolving if the hub is renamed, moved or dropped. Hard-coding the hub
 * route here would couple the child to it and break the standalone route that
 * the rollback path depends on.
 */
const buildUploadsHref = (pathname, csvPageId) =>
  pathname.includes('/csv-pages')
    ? `/admin/csv-uploads?csvPageId=${csvPageId}`
    : `${pathname}?tab=upload-history&csvPageId=${csvPageId}`;

const CSVPagesPage = () => {
  const { toast } = useToast();
  const { pathname } = useLocation();
  const [pages, setPages] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [agenciesError, setAgenciesError] = useState(false);
  const [search, setSearch] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    password: '',
    description: '',
    agencyId: '',
    isActive: true
  });

  const loadAgencies = useCallback(async () => {
    try {
      const response = await api.get('/admin/agencies');
      setAgencies(response.agencies || []);
      setAgenciesError(false);
    } catch (error) {
      // A failed fetch is NOT "this install has no agencies". Leaving the array
      // empty and saying nothing made the two indistinguishable -- and the edit
      // form below then offered "No Agency" as its ONLY choice, so one touch of
      // that control wrote a cleared entity for a page that had one.
      setAgencies([]);
      setAgenciesError(true);
    }
  }, []);

  const loadPages = useCallback(async (page = 1, searchTerm = '', agency = '') => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchTerm) params.append('search', searchTerm);
      if (agency) params.append('agencyId', agency);

      const response = await api.get(`/admin/csv-pages?${params}`);
      setPages(response.csvPages || []);
      setPagination(response.pagination);
    } catch (error) {
      setLoadError(true);
      setPages([]);
      toast.error('Failed to load CSV pages');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPages();
    loadAgencies();
  }, [loadPages, loadAgencies]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadPages(1, search, agencyFilter);
  };

  const handleOpenModal = (page = null) => {
    if (page) {
      setEditingPage(page);
      setFormData({
        name: page.name || '',
        slug: page.slug || '',
        password: '', // Don't populate password
        description: page.description || '',
        agencyId: page.agencyId || '',
        isActive: page.isActive !== false
      });
    } else {
      setEditingPage(null);
      setFormData({
        name: '',
        slug: '',
        password: '',
        description: '',
        agencyId: '',
        isActive: true
      });
    }
    setModalOpen(true);
  };

  const updateField = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug when name changes for new pages
      if (field === 'name' && !editingPage) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!editingPage && !formData.password) {
      toast.error('Password is required for new pages');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name),
        description: formData.description || null,
        // The admin never had a real choice while the list was missing, so do
        // not write one. `csvPageController` applies `agencyId` only when it is
        // present (:148), so omitting the key preserves the stored entity.
        ...(agenciesError ? {} : { agencyId: formData.agencyId || null }),
        isActive: formData.isActive
      };

      // Only include password if provided
      if (formData.password) {
        payload.password = formData.password;
      }

      if (editingPage) {
        await api.patch(`/admin/csv-pages/${editingPage.id}`, payload);
        toast.success('CSV page updated successfully');
      } else {
        await api.post('/admin/csv-pages', payload);
        toast.success('CSV page created successfully');
      }
      setModalOpen(false);
      loadPages(pagination?.page || 1, search, agencyFilter);
    } catch (error) {
      toast.error(error.message || 'Failed to save CSV page');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (pageId) => {
    if (!confirm('Are you sure you want to deactivate this CSV page?')) return;

    try {
      await api.delete(`/admin/csv-pages/${pageId}`);
      toast.success('CSV page deactivated');
      loadPages(pagination?.page || 1, search, agencyFilter);
    } catch (error) {
      toast.error('Failed to deactivate CSV page');
    }
  };

  const copyPublicLink = (slug) => {
    const link = `${window.location.origin}/csv/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>CSV Pages</h1>
          <p>Manage CSV upload pages for agencies and clients</p>
        </div>
        <Button onClick={() => handleOpenModal()}>Create New Page</Button>
      </div>

      {/* Search and Filters */}
      <Card className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <Input
            placeholder="Search by name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {agenciesError ? (
            <span className={styles.hint} role="alert">
              Agencies could not be loaded, so this filter is unavailable.
              <button type="button" className={styles.actionBtn} onClick={loadAgencies}>
                Try again
              </button>
            </span>
          ) : (
            <select
              className={styles.select}
              value={agencyFilter}
              onChange={(e) => {
                setAgencyFilter(e.target.value);
                loadPages(1, search, e.target.value);
              }}
            >
              <option value="">All Agencies</option>
              {agencies.map((agency) => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
          )}
          <Button type="submit">Search</Button>
        </form>
      </Card>

      {/* Pages Table */}
      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Agency</th>
                <th>Uploads</th>
                <th>Created</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page.id}>
                  <td>
                    <div className={styles.pageInfo}>
                      <div className={styles.pageName}>{page.name}</div>
                      {page.description && (
                        <div className={styles.pageDescription}>{page.description}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.slugCell}>
                      <code className={styles.slug}>/csv/{page.slug}</code>
                      <button
                        className={styles.copyBtn}
                        onClick={() => copyPublicLink(page.slug)}
                        title="Copy public link"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td>
                    {page.agency ? (
                      <span
                        className={styles.agencyBadge}
                        style={{ backgroundColor: page.agency.color || '#6366f1' }}
                      >
                        {page.agency.name}
                      </span>
                    ) : (
                      <span className={styles.noAgency}>—</span>
                    )}
                  </td>
                  <td>
                    <Link
                      className={styles.uploadLink}
                      to={buildUploadsHref(pathname, page.id)}
                      title={`View the uploads received through ${page.name}`}
                    >
                      <span className={styles.uploadCount}>{page.uploadCount ?? String.fromCharCode(8212)}</span>
                    </Link>
                  </td>
                  <td>{formatDate(page.createdAt)}</td>
                  <td>
                    <span className={`${styles.status} ${page.isActive ? styles.active : styles.inactive}`}>
                      {page.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleOpenModal(page)}
                      >
                        Edit
                      </button>
                      {page.isActive && (
                        <button
                          className={`${styles.actionBtn} ${styles.danger}`}
                          onClick={() => handleDeactivate(page.id)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && loadError && (
          <ListLoadError
            message="Could not load CSV upload portals"
            onRetry={() => loadPages(1, search, agencyFilter)}
          />
        )}

        {!loading && !loadError && pages.length === 0 && (
          <div className={styles.empty}>No CSV pages found</div>
        )}
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="outline"
            size="small"
            disabled={pagination.page <= 1}
            onClick={() => loadPages(pagination.page - 1, search, agencyFilter)}
          >
            Previous
          </Button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <Button
            variant="outline"
            size="small"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => loadPages(pagination.page + 1, search, agencyFilter)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingPage ? 'Edit CSV Page' : 'Create CSV Page'}
        size="medium"
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Name *</label>
            <Input
              placeholder="e.g., Partner Upload Portal"
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Slug</label>
            <div className={styles.slugInput}>
              <span className={styles.slugPrefix}>/csv/</span>
              <Input
                placeholder="partner-upload"
                value={formData.slug}
                onChange={(e) => updateField('slug', e.target.value)}
              />
            </div>
            <p className={styles.hint}>
              Public URL: {window.location.origin}/csv/{formData.slug || 'your-slug'}
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Password {editingPage ? '(leave blank to keep current)' : '*'}
            </label>
            <Input
              type="password"
              placeholder={editingPage ? 'Enter new password to change' : 'Minimum 6 characters'}
              value={formData.password}
              onChange={(e) => updateField('password', e.target.value)}
              required={!editingPage}
              minLength={formData.password ? 6 : undefined}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Agency (optional)</label>
            <select
              className={styles.select}
              value={formData.agencyId}
              onChange={(e) => updateField('agencyId', e.target.value)}
              disabled={agenciesError}
            >
              {agenciesError && formData.agencyId ? (
                <option value={formData.agencyId}>Current entity - name unavailable</option>
              ) : (
                <option value="">No Agency</option>
              )}
              {agencies.map((agency) => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
            {agenciesError && (
              <span className={styles.hint} role="alert">
                Agencies could not be loaded, so this cannot be changed. Saving will
                leave the current entity as it is.
                <button type="button" className={styles.actionBtn} onClick={loadAgencies}>
                  Try again
                </button>
              </span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Description (optional)</label>
            <textarea
              className={styles.textarea}
              placeholder="Instructions or notes for uploaders..."
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={3}
            />
          </div>

          {editingPage && (
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => updateField('isActive', e.target.checked)}
                />
                Page is active
              </label>
            </div>
          )}

          <div className={styles.formActions}>
            <Button
              variant="outline"
              type="button"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : editingPage ? 'Update Page' : 'Create Page'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CSVPagesPage;
